import {
  FilterTimeRange,
  ShiftEventsSortBy,
  SortOrder,
  EventSearchCriteria,
  Shift
} from '@hconnect/common/types'
import {generateTimeRange} from '@hconnect/common/utils'
import {Moment} from 'moment-timezone'

import {FilterOptions} from '../../types'

import {
  stoppageCodeSearchParamsToFilterParams,
  maintenanceNotificationSearchParamsToFilterParams
} from './stoppageHelper'

export type BuildEventSearchCriteriaParams = {
  shifts: Shift[]
  getPlantNow: () => Moment
  input: FilterOptions
  sortBy?: ShiftEventsSortBy
  sortOrder?: SortOrder
  pageNumber?: number
  itemsPerPage?: number
}

/**
 * Resolves the time range from FilterOptions input, handling both
 * explicit DateRange objects and ShiftQuickSelectSlots.
 */
export const resolveFilterTimeRange = (
  input: FilterOptions,
  shifts: Shift[],
  getPlantNow: () => Moment
): FilterTimeRange => {
  // Guard against empty shifts to prevent infinite recursion in generateTimeRange
  // When timeRange is an explicit date range object, shifts are not needed
  if (typeof input.timeRange === 'object') {
    return generateTimeRange(input.timeRange, shifts, getPlantNow)
  }
  if (shifts.length > 0) {
    return generateTimeRange(input.timeRange, shifts, getPlantNow)
  }
  return {startDate: null, endDate: null}
}

/**
 * Converts UI-level FilterOptions into an API-level EventSearchCriteria.
 * This bridges the gap between feature filter parameters and the actual POST body
 * expected by the `/shifthandover/{plantId}/search/events` endpoint.
 */
export const buildEventSearchCriteria = ({
  shifts,
  getPlantNow,
  input,
  sortBy = ShiftEventsSortBy.Details,
  sortOrder,
  pageNumber = 0,
  itemsPerPage = 50
}: BuildEventSearchCriteriaParams): EventSearchCriteria => {
  const timeRange = resolveFilterTimeRange(input, shifts, getPlantNow)

  return {
    timeRange,
    processStage: input.processStage,
    equipment: input.equipment,
    mainEquipment: input.mainEquipment,
    upmDepartmentIds:
      input.upmDepartmentIds || (input.stoppedMainEquipment && [input.stoppedMainEquipment.id]),
    eventType: input.eventType,
    priority: input.priority,
    status: input.status,
    category: input.category,
    changeFunctions: input.changeFunctions,
    pageNumber,
    itemsPerPage,
    sortBy,
    sortOrder,
    stoppageCodesFilter: stoppageCodeSearchParamsToFilterParams(input.stoppageCode),
    freeText: input.freeText,
    maintenanceNotificationExists: maintenanceNotificationSearchParamsToFilterParams(
      input.maintenanceNotificationExists
    )
  }
}
